import { Calendar, Clock3, RefreshCw } from 'lucide-react';
import { useEffect, useState } from 'react';

function toDateInputValue(date: Date) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
}

function toTimeInputValue(date: Date) {
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${hours}:${minutes}`;
}

function addHoursToTimeValue(time: string, hoursToAdd: number) {
    const [hours, minutes] = time.split(':').map(Number);
    if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return '';

    const totalMinutes = hours * 60 + minutes + hoursToAdd * 60;
    const normalizedMinutes = ((totalMinutes % (24 * 60)) + 24 * 60) % (24 * 60);
    const nextHours = Math.floor(normalizedMinutes / 60);
    const nextMinutes = normalizedMinutes % 60;
    return `${String(nextHours).padStart(2, '0')}:${String(nextMinutes).padStart(2, '0')}`;
}

function getDefaultRepublishDateTimes(now: Date) {
    const start = new Date(now);
    start.setSeconds(0, 0);
    start.setMinutes(0, 0, 0);

    if (now.getMinutes() >= 30) {
        start.setHours(now.getHours() + 1);
    } else {
        start.setHours(now.getHours());
    }

    const end = new Date(start);
    end.setHours(end.getHours() + 1);

    // Evitar que el fin sea del "siguiente día" (no soportado por el formulario actual).
    if (end.getDate() !== start.getDate()) {
        start.setDate(start.getDate() + 1);
        start.setHours(0, 0, 0, 0);
        end.setTime(start.getTime());
        end.setHours(1, 0, 0, 0);
    }

    return { start, end };
}

interface RepublishEventModalProps {
    isOpen: boolean;
    onClose: () => void;
    onConfirm: (data: { event_date: string; event_start_time: string; event_end_time: string }) => void;
    eventName: string;
    currentDate?: string;
    currentStartTime?: string;
    currentEndTime?: string;
    isRepublishing?: boolean;
}

export function RepublishEventModal({
    isOpen,
    onClose,
    onConfirm,
    eventName,
    currentDate,
    currentStartTime,
    currentEndTime,
    isRepublishing = false,
}: RepublishEventModalProps) {
    const [showIcon, setShowIcon] = useState(false);
    const [eventDate, setEventDate] = useState('');
    const [eventStartTime, setEventStartTime] = useState('');
    const [eventEndTime, setEventEndTime] = useState('');
    const [duration, setDuration] = useState('');
    const [endTimeTouched, setEndTimeTouched] = useState(false);

    // Bloquear scroll del body cuando el modal está abierto
    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }
        return () => {
            document.body.style.overflow = '';
        };
    }, [isOpen]);

    // Animación del icono al abrir
    useEffect(() => {
        if (isOpen) {
            const timer = setTimeout(() => setShowIcon(true), 200);
            return () => clearTimeout(timer);
        } else {
            setShowIcon(false);
        }
    }, [isOpen]);

    // Calcular duración cuando cambien las horas
    // Valores por defecto al abrir: fecha actual + hora redondeada + 1h de duracion
    useEffect(() => {
        if (!isOpen) return;

        const { start, end } = getDefaultRepublishDateTimes(new Date());
        setEventDate(toDateInputValue(start));
        setEventStartTime(toTimeInputValue(start));
        setEventEndTime(toTimeInputValue(end));
        setEndTimeTouched(false);
    }, [isOpen]);

    // Calcular duracion cuando cambien las horas
    useEffect(() => {
        if (eventStartTime && eventEndTime) {
            const [startHours, startMinutes] = eventStartTime.split(':').map(Number);
            const [endHours, endMinutes] = eventEndTime.split(':').map(Number);

            const startTotalMinutes = startHours * 60 + startMinutes;
            const endTotalMinutes = endHours * 60 + endMinutes;
            const diffMinutes = endTotalMinutes - startTotalMinutes;

            if (diffMinutes > 0) {
                const hours = Math.floor(diffMinutes / 60);
                const minutes = diffMinutes % 60;
                setDuration(hours > 0 ? `${hours}h ${minutes}min` : `${minutes}min`);
            } else {
                setDuration('');
            }
        } else {
            setDuration('');
        }
    }, [eventStartTime, eventEndTime]);

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();

        if (!eventDate || !eventStartTime || !eventEndTime) {
            alert('Por favor completa todos los campos');
            return;
        }

        // Validar que end > start
        const [startHours, startMinutes] = eventStartTime.split(':').map(Number);
        const [endHours, endMinutes] = eventEndTime.split(':').map(Number);
        const startTotalMinutes = startHours * 60 + startMinutes;
        const endTotalMinutes = endHours * 60 + endMinutes;

        if (endTotalMinutes <= startTotalMinutes) {
            alert('La hora de fin debe ser posterior a la de inicio');
            return;
        }

        // Validar que la fecha sea futura
        const [year, month, day] = eventDate.split('-').map(Number);
        const selectedDate = new Date(year, month - 1, day);
        selectedDate.setHours(0, 0, 0, 0);
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        if (selectedDate < today) {
            alert('La fecha debe ser futura');
            return;
        }

        onConfirm({ event_date: eventDate, event_start_time: eventStartTime, event_end_time: eventEndTime });
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center overflow-hidden p-4">
            {/* Backdrop */}
            <div
                className="absolute inset-0 bg-black/50 backdrop-blur-sm"
                onClick={onClose}
            />

            {/* Modal */}
            <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden animate-in fade-in zoom-in-95 duration-300 max-h-[90vh] overflow-y-auto">
                {/* Header con fondo púrpura e icono */}
                <div className="relative h-40 bg-gradient-to-b from-purple-50 via-purple-50/80 to-white flex items-center justify-center overflow-hidden">
                    {/* Decoraciones */}
                    <div className="absolute inset-0 pointer-events-none">
                        <span className="absolute top-6 left-10 text-purple-300 text-lg font-light">+</span>
                        <span className="absolute top-4 right-12 text-purple-300 text-sm font-light">+</span>
                        <span className="absolute top-12 right-8 text-purple-200 text-xs font-light">+</span>
                        <span className="absolute bottom-12 left-8 text-purple-200 text-xs font-light">+</span>
                        <span className="absolute top-8 left-16 w-1.5 h-1.5 bg-purple-200 rounded-full" />
                        <span className="absolute top-10 right-16 w-1 h-1 bg-purple-300 rounded-full" />
                        <span className="absolute bottom-16 left-12 w-1 h-1 bg-purple-200 rounded-full" />
                        <span className="absolute bottom-10 right-10 w-1.5 h-1.5 bg-purple-200 rounded-full" />
                    </div>

                    {/* Círculos animados */}
                    <div className="absolute inset-0 flex items-center justify-center">
                        <div className={`absolute w-28 h-28 rounded-full border-2 border-purple-200/40 transition-all duration-700 ${showIcon ? 'scale-100 opacity-100' : 'scale-50 opacity-0'}`} />
                        <div className={`absolute w-20 h-20 rounded-full border-2 border-purple-300/50 transition-all duration-500 delay-100 ${showIcon ? 'scale-100 opacity-100' : 'scale-50 opacity-0'}`} />
                        <div className={`absolute w-14 h-14 rounded-full bg-purple-100/60 transition-all duration-300 delay-150 ${showIcon ? 'scale-100 opacity-100' : 'scale-50 opacity-0'}`} />
                    </div>

                    {/* Iconos superpuestos */}
                    <div className={`relative flex items-center gap-1 transition-all duration-500 ${showIcon ? 'scale-100 opacity-100' : 'scale-0 opacity-0'}`}>
                        <div className="w-12 h-12 rounded-full bg-purple-500 flex items-center justify-center shadow-lg shadow-purple-500/30">
                            <Calendar className="w-6 h-6 text-white" strokeWidth={2} />
                        </div>
                        <RefreshCw className="w-5 h-5 text-purple-600" strokeWidth={2.5} />
                    </div>
                </div>

                {/* Contenido */}
                <form onSubmit={handleSubmit} className="px-6 pb-6">
                    {/* Título */}
                    <h2 className="text-xl font-bold text-slate-900 text-center mb-2">
                        Volver a publicar evento
                    </h2>

                    {/* Subtítulo */}
                    <p className="text-slate-500 text-center text-sm mb-4">
                        Ingresa la nueva fecha y horarios para volver a publicar el evento
                    </p>

                    {/* Evento actual (colapsable) */}
                    <details className="mb-4">
                        <summary className="text-xs font-medium text-slate-600 cursor-pointer hover:text-slate-900 transition-colors">
                            Ver fecha/hora actual del evento
                        </summary>
                        <div className="mt-2 bg-slate-50 border border-slate-200 rounded-lg p-2 text-xs text-slate-600">
                            <p><strong>Evento:</strong> {eventName}</p>
                            {currentDate && <p><strong>Fecha:</strong> {currentDate}</p>}
                            {currentStartTime && currentEndTime && (
                                <p><strong>Horario:</strong> {currentStartTime} - {currentEndTime}</p>
                            )}
                        </div>
                    </details>

                    {/* Formulario */}
                    <div className="space-y-4 mb-5">
                        {/* Fecha */}
                        <div>
                            <label htmlFor="event_date" className="block text-sm font-medium text-slate-700 mb-1.5">
                                Nueva fecha del evento <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="date"
                                id="event_date"
                                value={eventDate}
                                onChange={(e) => setEventDate(e.target.value)}
                                min={toDateInputValue(new Date())}
                                required
                                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                            />
                        </div>

                        {/* Hora inicio */}
                        <div>
                            <label htmlFor="event_start_time" className="block text-sm font-medium text-slate-700 mb-1.5">
                                Hora de inicio <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="time"
                                id="event_start_time"
                                value={eventStartTime}
                                onChange={(e) => {
                                    const nextStartTime = e.target.value;
                                    setEventStartTime(nextStartTime);
                                    if (!endTimeTouched) {
                                        setEventEndTime(addHoursToTimeValue(nextStartTime, 1));
                                    }
                                }}
                                required
                                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                            />
                        </div>

                        {/* Hora fin */}
                        <div>
                            <label htmlFor="event_end_time" className="block text-sm font-medium text-slate-700 mb-1.5">
                                Hora de fin <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="time"
                                id="event_end_time"
                                value={eventEndTime}
                                onChange={(e) => {
                                    setEndTimeTouched(true);
                                    setEventEndTime(e.target.value);
                                }}
                                required
                                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                            />
                        </div>

                        {/* Duración calculada */}
                        {duration && (
                            <div className="text-sm text-purple-600 font-medium inline-flex items-center gap-2">
                                <Clock3 className="w-4 h-4" />
                                Duración: {duration}
                            </div>
                        )}
                    </div>

                    {/* Botones */}
                    <div className="flex gap-3">
                        <button
                            type="button"
                            onClick={onClose}
                            disabled={isRepublishing}
                            className="flex-1 py-2.5 px-4 bg-white border-2 border-slate-200 hover:border-slate-300 text-slate-700 font-medium rounded-xl transition-colors disabled:opacity-50"
                        >
                            Cancelar
                        </button>
                        <button
                            type="submit"
                            disabled={isRepublishing}
                            className="flex-1 py-2.5 px-4 bg-purple-500 hover:bg-purple-600 text-white font-medium rounded-xl transition-colors shadow-lg shadow-purple-500/20 disabled:opacity-50"
                        >
                            {isRepublishing ? 'Publicando...' : 'Volver a Publicar'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}
